import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { RefreshCw, ShieldCheck } from "lucide-react";

/**
 * Lightweight math CAPTCHA — no external service.
 *
 * Wire-up:
 *   const captcha = useCaptcha();
 *   <MathCaptcha {...captcha} />
 *   await api.post("/auth/login", body, { headers: captcha.headers() });
 *
 * The backend endpoints that require it read:
 *   X-Captcha-Token: <token from GET /security/captcha>
 *   X-Captcha-Answer: <user's typed answer>
 *
 * On backend rejection (400), call `captcha.refresh()` to fetch a new
 * challenge — tokens are single-use in practice because the operator
 * has to type a fresh answer.
 */
export function useCaptcha() {
  const [question, setQuestion] = useState("");
  const [token, setToken] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    setAnswer("");
    setToken("");
    try {
      const { data } = await api.get("/security/captcha");
      setQuestion(data.question || "");
      setToken(data.token || "");
    } catch (err) {
      setError(err?.response?.status === 429
        ? "Too many requests. Wait a minute, then get a new question."
        : "Couldn't load the security check. Use the refresh button to try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const headers = useCallback(
    () => ({
      "X-Captcha-Token": token,
      "X-Captcha-Answer": (answer || "").trim(),
    }),
    [token, answer],
  );

  const isReady = Boolean(token) && (answer || "").trim().length > 0;

  return {
    question,
    token,
    answer,
    setAnswer,
    loading,
    error,
    refresh,
    headers,
    isReady,
  };
}

export function MathCaptcha({
  question,
  answer,
  setAnswer,
  loading,
  error,
  refresh,
  label = "Security check",
  testId = "captcha",
}) {
  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
      data-testid={`${testId}-wrap`}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
        {label}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <div
          className="flex-1 select-none rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-200"
          data-testid={`${testId}-question`}
        >
          {loading ? "Loading…" : question || "—"}
        </div>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Answer"
          className="w-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="rounded-md border border-slate-300 bg-white p-2 text-slate-600 transition hover:bg-slate-100 disabled:opacity-40"
          title="Get a new question"
          data-testid={`${testId}-refresh`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {error && (
        <div className="mt-1.5 text-xs text-rose-600" data-testid={`${testId}-error`}>
          {error}
        </div>
      )}
    </div>
  );
}
